import { watch } from "vue";

import { ensureTranslationNamespaces, language } from "@core/composables/state/useLanguage";
import { applyDocumentTitle } from "@core/utils/documentTitle";
import { createRouter, createWebHistory, type RouteLocationNormalized } from "vue-router";

import { translate } from "@common/i18n";

import { createAuthGuard, createTranslationGuard } from "./guards";
import { createBrowserUpgradeTerminalGuard } from "./guards/browserUpgradeTerminalGuard";
import { routes } from "./routes";
import { queueDashboardScrollReset, resolveScrollGroup, shouldForceScrollReset } from "./utils";

import { resolveNamespacesForRoute } from "@/translations";

import "./types";

export { resetDashboardScrollPosition, queueDashboardScrollReset } from "./utils";
export { lotConfigurationTabRouteByKey } from "./routes";

const accountSegment = "/dashboard";
const pathname = window.location.pathname;
const segmentIndex = pathname.indexOf(accountSegment);
const base = segmentIndex === -1 ? accountSegment : pathname.slice(0, segmentIndex + accountSegment.length);

export const router = createRouter({
  history: createWebHistory(base),
  routes,
  scrollBehavior(to: RouteLocationNormalized, from: RouteLocationNormalized, savedPosition) {
    if (shouldForceScrollReset(to)) {
      queueDashboardScrollReset();
      return { left: 0, top: 0 };
    }

    if (savedPosition) {
      return savedPosition;
    }

    if (to.hash) {
      return { el: to.hash };
    }

    const toGroup = resolveScrollGroup(to);
    const fromGroup = resolveScrollGroup(from);

    const toHasParams = Object.keys(to.params).length > 0;
    const shouldPreserve = toHasParams && toGroup !== undefined && toGroup === fromGroup;

    if (shouldPreserve) {
      return undefined;
    }

    queueDashboardScrollReset();

    return { left: 0, top: 0 };
  }
});

router.beforeResolve(createTranslationGuard());
router.beforeEach(createBrowserUpgradeTerminalGuard());
router.beforeEach(createAuthGuard(base));

const initialTitle = document.title;

const updateDocumentTitle = () => {
  const currentRoute = router.currentRoute.value;
  const titleKey = currentRoute.meta.titleKey;
  if (titleKey) {
    let translatedTitle = translate(titleKey);
    const dashboardParam = currentRoute.params.dashboard;
    if (currentRoute.name === "statistics" && typeof dashboardParam === "string" && dashboardParam) {
      translatedTitle = `${translatedTitle} - ${translate(`statistics.dashboards.${dashboardParam}`)}`;
    }
    applyDocumentTitle(translatedTitle);
    return;
  }
  applyDocumentTitle(initialTitle);
};

router.afterEach(() => {
  updateDocumentTitle();
});

watch(language, async () => {
  updateDocumentTitle();
  const currentName = typeof router.currentRoute.value.name === "string" ? router.currentRoute.value.name : null;
  const namespaces = resolveNamespacesForRoute(currentName);
  if (namespaces.length > 0) {
    try {
      await ensureTranslationNamespaces(namespaces);
    } catch (error) {
      console.error("[App] Failed to reload translations for current route", error);
    }
  }
});

export default router;
